/**
 * Route constants for the aluminum factory application.
 * Following Turkish naming conventions as requested.
 */
export const ROUTE_PATHS = {
  // Turkish Routes
  HOME: "/",
  COMPANY: "/kurumsal",
  PRODUCTION: "/uretim",
  PRODUCTION_FOUNDRY: "/uretim/dokumhane",
  PRODUCTION_EXTRUSION: "/uretim/ekstruzyon",
  PRODUCTION_COATING: "/uretim/yuzey-islem",
  PRODUCTION_MECHANICAL: "/uretim/mekanik-islem",
  PRODUCTION_MOLD: "/uretim/kalip-imalati",
  PRODUCTS: "/urunler",
  PRODUCTS_ARCHITECTURAL: "/urunler/mimari-sistemler",
  PRODUCTS_INDUSTRIAL: "/urunler/endustriyel-profiller",
  SUSTAINABILITY: "/surdurulebilirlik",
  CONTACT: "/iletisim",
  PROFILE_GUIDE: "/profil-rehberi",
  DOWNLOAD_CENTER: "/indirilebilirler",
  
  // English Routes
  HOME_EN: "/en",
  COMPANY_EN: "/en/about",
  PRODUCTION_EN: "/en/production",
  PRODUCTION_FOUNDRY_EN: "/en/production/foundry",
  PRODUCTION_EXTRUSION_EN: "/en/production/extrusion",
  PRODUCTION_COATING_EN: "/en/production/coating",
  PRODUCTION_MECHANICAL_EN: "/en/production/mechanical",
  PRODUCTION_MOLD_EN: "/en/production/mold",
  PRODUCTS_EN: "/en/products",
  PRODUCTS_ARCHITECTURAL_EN: "/en/products/architectural",
  PRODUCTS_INDUSTRIAL_EN: "/en/products/industrial",
  SUSTAINABILITY_EN: "/en/sustainability",
  CONTACT_EN: "/en/contact",
  PROFILE_GUIDE_EN: "/en/profile-guide",
  DOWNLOAD_CENTER_EN: "/en/downloads",
  
  // Spanish Routes
  HOME_ES: "/es",
  COMPANY_ES: "/es/empresa",
  PRODUCTION_ES: "/es/produccion",
  PRODUCTION_FOUNDRY_ES: "/es/produccion/fundicion",
  PRODUCTION_EXTRUSION_ES: "/es/produccion/extrusion",
  PRODUCTION_COATING_ES: "/es/produccion/recubrimiento",
  PRODUCTION_MECHANICAL_ES: "/es/produccion/mecanico",
  PRODUCTION_MOLD_ES: "/es/produccion/molde",
  PRODUCTS_ES: "/es/productos",
  PRODUCTS_ARCHITECTURAL_ES: "/es/productos/arquitectonicos",
  PRODUCTS_INDUSTRIAL_ES: "/es/productos/industriales",
  SUSTAINABILITY_ES: "/es/sostenibilidad",
  CONTACT_ES: "/es/contacto",
  PROFILE_GUIDE_ES: "/es/guia-perfiles",
  DOWNLOAD_CENTER_ES: "/es/descargas",
  
  // Product Detail Pages
  ARCHITECTURAL_SYSTEMS: "/mimari-sistemler",
  INDUSTRIAL_PROFILES: "/endustriyel-profiller",
  ARCHITECTURAL_SYSTEMS_EN: "/en/architectural-systems",
  INDUSTRIAL_PROFILES_EN: "/en/industrial-profiles",
  ARCHITECTURAL_SYSTEMS_ES: "/es/sistemas-arquitectonicos",
  INDUSTRIAL_PROFILES_ES: "/es/perfiles-industriales",
  FACADE_SYSTEMS: "/cephe-dograma-sistemleri",
  FACADE_SYSTEMS_EN: "/en/facade-systems",
  FACADE_SYSTEMS_ES: "/es/sistemas-fachada",
  ALUMINUM_BILLET: "/tr/aluminyum-billet",
  ALUMINUM_BILLET_EN: "/en/aluminum-billet",
} as const;

/**
 * Represents a product category (e.g., Architectural Systems).
 */
export interface ProductCategory {
  id: string;
  title: string;
  description: string;
  image: string;
  features: string[];
  path: string;
}

/**
 * Represents a specific production line or facility (e.g., 8" Extrusion Line).
 */
export interface ProductionLine {
  id: string;
  title: string;
  capacity: string;
  description: string;
  image: string;
  specifications: string[];
  type: "extrusion" | "foundry" | "coating" | "mechanical";
}

/**
 * Represents corporate statistics for display in counters or stats grids.
 */
export interface CompanyStats {
  id: string;
  label: string;
  value: string;
  suffix?: string;
  description?: string;
}

/**
 * Represents a team member for the leadership section.
 */
export interface TeamMember {
  id: string;
  name: string;
  role: string;
  image: string;
}

/**
 * Represents a downloadable document in the Download Center.
 */
export interface DownloadItem {
  id: string;
  title: string;
  category: "katalog" | "teknik" | "sertifika";
  fileSize: string;
  updatedAt: string;
  url: string;
}

/**
 * Utility to smoothly scroll to a section on the same page.
 */
export const scrollToSection = (elementId: string) => {
  const element = document.getElementById(elementId);
  if (element) {
    const headerOffset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
};

/**
 * Simple slugification for URLs or IDs.
 */
export const slugify = (text: string) => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-");
};
